// src/components/BibleStudyModal.js
// Updated: 08/24/2025 08:58 PM

import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

function BibleStudyModal({ open, onClose, onSave, bibleStudy: initialBibleStudy }) {
  const [title, setTitle] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [date, setDate] = useState(null);
  const [description, setDescription] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialBibleStudy) {
      setTitle(initialBibleStudy.title || '');
      setSpeaker(initialBibleStudy.speaker || '');
      setDate(initialBibleStudy.date ? initialBibleStudy.date.toDate() : null);
      setDescription(initialBibleStudy.description || '');
      setVideoLink(initialBibleStudy.videoLink || '');
    } else {
      setTitle('');
      setSpeaker('');
      setDate(null);
      setDescription('');
      setVideoLink('');
    }
    setErrors({});
  }, [initialBibleStudy, open]);

  const validate = () => {
    let tempErrors = {};
    let isValid = true;

    if (!title) {
      tempErrors.title = "Title is required.";
      isValid = false;
    }
    if (!speaker) {
      tempErrors.speaker = "Teacher is required.";
      isValid = false;
    }
    if (!date) {
      tempErrors.date = "Date is required.";
      isValid = false;
    }
    if (!videoLink) {
      tempErrors.videoLink = "Video link is required.";
      isValid = false;
    } else if (!/^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_.~#?&//=]*)$/.test(videoLink)) {
      tempErrors.videoLink = "Please enter a valid URL.";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const bibleStudyData = {
      title,
      speaker,
      date,
      description,
      videoLink,
    };

    try {
      await onSave(bibleStudyData);
      onClose();
    } catch (error) {
      console.error("Error saving Bible study:", error);
      // Handle error display to user if necessary
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {initialBibleStudy ? 'Edit Bible Study' : 'Add New Bible Study'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Bible Study Title"
            type="text"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Teacher"
            type="text"
            fullWidth
            variant="outlined"
            value={speaker}
            onChange={(e) => setSpeaker(e.target.value)}
            error={!!errors.speaker}
            helperText={errors.speaker}
            sx={{ mb: 2 }}
          />
          <DatePicker
            label="Date of Bible Study"
            value={date}
            onChange={(newValue) => setDate(newValue)}
            slots={{ textField: TextField }}
            slotProps={{ textField: { fullWidth: true, margin: "dense", sx: { mb: 2 }, error: !!errors.date, helperText: errors.date } }}
            enableAccessibleFieldDOMStructure={false}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Video Link (e.g., Facebook Live URL)"
            type="url"
            fullWidth
            variant="outlined"
            value={videoLink}
            onChange={(e) => setVideoLink(e.target.value)}
            error={!!errors.videoLink}
            helperText={errors.videoLink}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {initialBibleStudy ? 'Save Changes' : 'Add Bible Study'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default BibleStudyModal;